import React, { FC, useMemo, useState } from "react";

import fileDownload from "js-file-download";
import { useTranslation } from "react-i18next";

import { ReactComponent as ReportIcon } from "assets/images/report.svg";
import Button from "components/atoms/Button";

import { downloadStudentReport } from "api/downloadReport";

import { useToast } from "hooks/Toast";
import { useHistory } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import { UserPlan } from "types/user";
import BookSessionModal from "pages/Student/Dashboard/Modals/BookSessionModal";
import {
  FaqIcon,
  ExternalLinkIcon,
  ButtonContainer,
  InfoContainer,
  AssessmentInfo,
  AvailableAssessments,
  RoundedButton,
  ReportContainer,
  ReportTop,
  ReportBadge,
  ReportDownloadText,
  MobileOnly,
  TutorialIcon,
} from "./styles";

import { Props } from "./types";
import ViewerAccessModalContent from "../ViewerAccessModalContent";
import { ViewerAccessModal } from "../ReportDashboard/styles";

const StudentInfo: FC<Props> = ({
  reportName,
  welcomeText,
  finished,
  studentId,
  testId,
  navigateToVideo,
  isTutorialButtonVisible,
  isFreeTest,
  isPaidTest,
  isInterestInventoryFinished,
  isBookExpertSessionButtonVisible,
  handleOpenBookSessionModal,
}) => {
  const { t } = useTranslation();
  const Toast = useToast();
  const history = useHistory();

  const [isViewerAccessModalOpen, setIsViewerAccessModalOpen] = useState(false);

  const handleDownloadReport = (id: string) => {
    downloadStudentReport(
      id,
      (res) => {
        fileDownload(res.data, reportName);
        Toast("success", t("successAction.reportDownloading"));
      },
      () => {
        Toast("error", t("errorAction.reportDoesntExist"));
      }
    );
  };

  const handleFaq = () => {
    window.open("https://careerfit.com/faq", "_blank");
  };

  return (
    <InfoContainer>
      {finished ? (
        <>
          <ButtonContainer style={{ flexDirection: "column", gap: "1.6rem" }}>
            {isBookExpertSessionButtonVisible && handleOpenBookSessionModal && (
              <Button
                variant="primary"
                width="auto"
                onClick={handleOpenBookSessionModal}
              >
                {t("student.bookExpertSession")}
              </Button>
            )}
            <Button
              variant="secondary"
              width="100%"
              onClick={() => handleDownloadReport(studentId || "")}
            >
              Download Report
            </Button>
            <Button
              variant="secondary"
              width="100%"
              onClick={() => {
                setIsViewerAccessModalOpen(true);
              }}
            >
              {t("student.shareDashboard")}
            </Button>
          </ButtonContainer>
          <ReportContainer>
            <ReportTop>
              {t("student.reportIsReady")}
              <ReportBadge>
                <ReportIcon />
              </ReportBadge>
            </ReportTop>
            <ReportDownloadText>
              {t("student.downloadReport")}
            </ReportDownloadText>
            <Button
              variant="primary"
              onClick={() => handleDownloadReport(studentId || "")}
            >
              {t("student.downloadYourReport")}
            </Button>
          </ReportContainer>
        </>
      ) : (
        <>
          <AvailableAssessments>
            {!isFreeTest && isPaidTest ? welcomeText : "Upgrade CareerFit"}
          </AvailableAssessments>
          {!isFreeTest && isPaidTest && (
            <AssessmentInfo>
              {t("student.shareReportInfo_1")}
              <br />
              <br />
              {t("student.shareReportInfo_2")}
              <br />
              <br />
              {t("student.shareReportInfo_3")}
              <br />
              <br />
              {t("student.shareReportInfo_4")}
              <br />
              <br />
              {t("student.shareReportInfo_5")}
            </AssessmentInfo>
          )}
          {isFreeTest && (
            <AssessmentInfo>
              <div>
                When you complete all the assessments, your report will be
                available here.
              </div>
              <div style={{ marginTop: "50px" }}>
                <Button
                  variant="secondary"
                  onClick={() => history.push("/individual/registration")}
                >
                  Create Free Account
                </Button>
              </div>
            </AssessmentInfo>
          )}
          {!isFreeTest && !isPaidTest && (
            <AssessmentInfo>
              <div>
                Complete the Aptitude Assessments & Unlock specific career
                suggestions ideally suited to you.
              </div>
              <div style={{ marginTop: "50px" }}>
                {!isInterestInventoryFinished && (
                  <ReactTooltip
                    id="disabled-dashboard-button-tooltip"
                    place="top"
                    type="dark"
                    effect="float"
                  >
                    <span>
                      Finish your Interest Inventory to see the Dashboard
                    </span>
                  </ReactTooltip>
                )}
                <span data-tip data-for="disabled-dashboard-button-tooltip">
                  <Button
                    variant="primary"
                    onClick={() =>
                      history.push("/student/interest-inventory/results")
                    }
                    disabled={!isInterestInventoryFinished}
                  >
                    Go to Dashboard
                  </Button>
                </span>
              </div>
            </AssessmentInfo>
          )}
        </>
      )}

      {isTutorialButtonVisible && (
        <ButtonContainer>
          <RoundedButton isActive onClick={navigateToVideo}>
            <TutorialIcon />
          </RoundedButton>
          <span>{t("student.tutorialWatch")}</span>
        </ButtonContainer>
      )}
      <ButtonContainer>
        <RoundedButton isActive={!finished} onClick={handleFaq}>
          <FaqIcon $isActive={!finished} />
        </RoundedButton>
        <span>
          {t("student.readFaq")}
          <ExternalLinkIcon />
        </span>
      </ButtonContainer>

      <ViewerAccessModal
        size="narrow"
        isOpen={isViewerAccessModalOpen}
        title="Share your results"
        onClose={() => setIsViewerAccessModalOpen(false)}
      >
        <ViewerAccessModalContent
          testId={testId || ""}
          onClose={() => setIsViewerAccessModalOpen(false)}
        />
      </ViewerAccessModal>
    </InfoContainer>
  );
};

export default StudentInfo;
